import React from "react";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { Card, FcDrawer, Icon, inputClass } from "../schedule/ui";
import { apiResult, COUNTRY_CODES } from "./memberUtils";

/**
 * Import members from another system (new design). Same flow as the old MigrateMultipleMembers:
 * pick the credit pass they had (same product as in the old system, any price), load a CSV or type rows,
 * then POST bookings/product-migration {productId, userList} in chunks of 2 rows. Each row gets an account
 * and the credits on that pass. The backend books it as a free migration (no payment, no revenue).
 * The backend rejects a request with a row of 0 credits, so each row needs 1 or more.
 */

const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const HEADERS = ["countryCode", "phoneNumber", "fullName", "email", "migrationCredit"];
const CHUNK = 2;
let nextId = 1;
const blankRow = () => ({ id: nextId++, countryCode: "+31", phoneNumber: "", fullName: "", email: "", migrationCredit: "", status: "" });

const rowErrors = (r) => {
  const e = {};
  if (!/^\+\d{1,4}$/.test(r.countryCode || "")) e.countryCode = "Code like +31";
  if (!/^\d{6,11}$/.test(String(r.phoneNumber || "").replace(/\s/g, ""))) e.phoneNumber = "6–11 digits";
  if (!String(r.fullName || "").trim()) e.fullName = "Required";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(r.email || "").trim())) e.email = "Check this email";
  if (!/^\d+$/.test(String(r.migrationCredit ?? "")) || parseInt(r.migrationCredit, 10) < 1) e.migrationCredit = "1 or more";
  return e;
};

const parseLine = (line) => {
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"' && q && line[i + 1] === '"') {
      cur += '"';
      i++;
    } else if (ch === '"') q = !q;
    else if (ch === "," && !q) {
      out.push(cur.trim());
      cur = "";
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

const parseCSV = (text) => {
  const lines = text.replace(/\r/g, "").trim().split("\n").filter((l) => l.trim());
  if (!lines.length) throw new Error("The file is empty.");
  const headers = parseLine(lines[0]);
  const missing = HEADERS.filter((h) => !headers.includes(h));
  if (missing.length) throw new Error(`The file is missing these columns: ${missing.join(", ")}. Use the template.`);
  return lines.slice(1).map((line) => {
    const vals = parseLine(line);
    const r = blankRow();
    headers.forEach((h, i) => {
      if (HEADERS.includes(h)) r[h] = (vals[i] || "").trim();
    });
    if (r.countryCode && !r.countryCode.startsWith("+")) r.countryCode = `+${r.countryCode}`;
    return r;
  });
};

export default function ImportMembersDrawer({ open, onClose, onImported = () => null }) {
  const { addSuccessToast } = useToast();
  const [passes, setPasses] = React.useState([]);
  const [passId, setPassId] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [fileName, setFileName] = React.useState("");
  const [error, setError] = React.useState("");
  const [showErrors, setShowErrors] = React.useState(false);
  const [running, setRunning] = React.useState(false);
  const fileRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    setRows([blankRow()]);
    setFileName("");
    setError("");
    setShowErrors(false);
    setRunning(false);
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}products/list?getall=false&productType=classpass` });
        const [ok, data] = apiResult(res);
        const list = ok && Array.isArray(data) ? data.filter((p) => p.productName !== "free trial credit") : [];
        setPasses(list);
        setPassId((cur) => (list.some((p) => p._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setPasses([]);
      }
    })();
  }, [open]);

  const update = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v, status: r.status === "done" ? "done" : "" } : r)));
  const pending = rows.filter((r) => r.status !== "done");
  const invalid = pending.filter((r) => Object.keys(rowErrors(r)).length);
  const credits = pending.reduce((n, r) => n + (parseInt(r.migrationCredit, 10) || 0), 0);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    if (!file.name.toLowerCase().endsWith(".csv")) return setError("Only CSV files can be imported.");
    if (file.size > MAX_SIZE_BYTES) return setError("The file is bigger than 5 MB.");
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(String(ev.target.result || ""));
        if (!parsed.length) return setError("The file has no rows under the header.");
        setRows(parsed);
        setFileName(file.name);
        setShowErrors(true);
      } catch (err) {
        setError(err.message || "Couldn't read this file.");
      }
    };
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csv = `${HEADERS.join(",")}\n+31,612345678,Emma de Jong,emma@example.com,5\n`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "fitclub-members-template.csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const run = async () => {
    setShowErrors(true);
    setError("");
    if (!passId) return setError("Pick the pass that holds their credits. Create the same pass under My Products first if there is none.");
    if (!pending.length) return setError("Add at least one member.");
    if (invalid.length) return setError(`Fix ${invalid.length} ${invalid.length === 1 ? "row" : "rows"} first.`);
    setRunning(true);
    let added = 0;
    let failed = 0;
    for (let i = 0; i < pending.length; i += CHUNK) {
      const chunk = pending.slice(i, i + CHUNK);
      const ids = chunk.map((r) => r.id);
      setRows((rs) => rs.map((r) => (ids.includes(r.id) ? { ...r, status: "sending" } : r)));
      let ok = false;
      let msg = "";
      let rowResults = [];
      try {
        const res = await axiosInstance({
          method: "post",
          url: `${getUrlForBookings()}product-migration`,
          data: {
            productId: passId,
            userList: chunk.map((r) => ({
              id: r.id,
              countryCode: r.countryCode,
              phoneNumber: String(r.phoneNumber).replace(/\s/g, ""),
              fullName: r.fullName.trim(),
              email: r.email.trim(),
              migrationCredit: parseInt(r.migrationCredit, 10),
              isValid: true,
              isSubmitted: false,
            })),
          },
        });
        let data;
        [ok, data, msg] = apiResult(res);
        rowResults = Array.isArray(data) ? data : [];
      } catch (e) {
        msg = "Couldn't reach the server.";
      }
      // The request can succeed while a row's credits were not added (the member account is created first),
      // so check each row's purchaseStatus instead of trusting the overall status.
      const outcome = (r) => {
        if (!ok) return { status: "failed", message: msg || "Couldn't import this row." };
        const res = rowResults.find((x) => String(x.id) === String(r.id)) || rowResults[chunk.indexOf(r)];
        if (res && res.purchaseStatus === false) return { status: "failed", message: "Member added, but the credits were not. Try again or use Give credits on their page." };
        return { status: "done", message: "" };
      };
      const results = chunk.map((r) => ({ id: r.id, ...outcome(r) }));
      const doneHere = results.filter((x) => x.status === "done").length;
      added += doneHere;
      failed += results.length - doneHere;
      setRows((rs) => rs.map((r) => {
        const x = results.find((y) => y.id === r.id);
        return x ? { ...r, status: x.status, message: x.message } : r;
      }));
    }
    setRunning(false);
    if (added) onImported();
    if (!failed) {
      addSuccessToast(`${added} ${added === 1 ? "member" : "members"} imported`);
      onClose();
    } else {
      setError(`${added} imported, ${failed} failed. Check the rows marked in red and try again.`);
    }
  };

  const errorsFor = (r) => (showErrors && r.status !== "done" ? rowErrors(r) : {});
  const cell = (r, k, props = {}) => {
    const err = errorsFor(r)[k];
    return (
      <div className="min-w-0">
        <input
          aria-label={props.label}
          value={r[k] ?? ""}
          disabled={running || r.status === "done"}
          onChange={(e) => update(r.id, k, e.target.value)}
          inputMode={props.inputMode}
          placeholder={props.placeholder}
          className={classNames(inputClass(err), "h-10 rounded-xl px-3 text-sm disabled:bg-[#F7F4F8]")}
        />
        {err && <div className="mt-1 text-[11px] font-bold text-red-600">{err}</div>}
      </div>
    );
  };

  return (
    <FcDrawer
      open={open}
      onClose={() => !running && onClose()}
      eyebrow="IMPORT MEMBERS"
      title="Bring members over from another system"
      subtitle="Each member gets an account and the credits they still had with you."
      primaryLabel={running ? "Importing…" : `Import ${pending.length} ${pending.length === 1 ? "member" : "members"}`}
      onPrimary={run}
      busy={running}
      primaryDisabled={!pending.length}
      wide
      footerNote={<span className="hidden sm:inline">{credits} credits{invalid.length && showErrors ? ` · ${invalid.length} to fix` : ""}</span>}
    >
      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">{error}</div>}
      <Card title="1. Which pass holds their credits?">
        <p className="-mt-2 mb-3 text-[13px] text-gray-500">Pick the same pass they had with you, for example "10-session pass". They get their remaining credits on it. Nothing is charged and it doesn't count as revenue. Members with 0 credits left can't be imported; invite them instead.</p>
        {passes.length ? (
          <select aria-label="Credit pass" value={passId} onChange={(e) => setPassId(e.target.value)} className={classNames(inputClass(false), "sm:max-w-sm")}>
            {passes.map((p) => (
              <option key={p._id} value={p._id}>
                {p.productName || p.productFields?.passName}
                {parseFloat(p.price) > 0 ? ` · €${parseFloat(p.price).toFixed(2).replace(".", ",")}` : " · free"}
              </option>
            ))}
          </select>
        ) : (
          <p className="rounded-2xl bg-[#FFF4E5] px-4 py-3 text-sm font-semibold text-[#7A4A00]">You have no credit pass yet. Create the same passes your members have now under My Products first.</p>
        )}
      </Card>
      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-black text-gray-900">2. Add the members</h3>
          <button type="button" onClick={downloadTemplate} className="text-sm font-extrabold text-indigo-bz hover:underline">Download CSV template</button>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={running}
          className="flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-[#E3D0F7] bg-[#FBF8FE] p-4 text-left hover:bg-[#F3E9FF]"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
            <Icon className="h-5 w-5" d={<><path d="M12 15V3M7 8l5-5 5 5" /><path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /></>} />
          </span>
          <span>
            <span className="block text-[15px] font-extrabold text-gray-900">{fileName ? `Loaded ${fileName}` : "Choose a CSV file"}</span>
            <span className="block text-[13px] text-gray-500">Columns: countryCode, phoneNumber, fullName, email, migrationCredit. Max 5 MB.</span>
          </span>
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />

        <div className="mt-4 overflow-x-auto rounded-2xl border border-gray-200">
          <div className="min-w-[640px]">
            <div className="grid grid-cols-[96px_minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,1.6fr)_80px_40px] gap-2 bg-[#FBF9FC] px-3 py-2.5 text-[11px] font-extrabold uppercase tracking-wide text-gray-500">
              <span>Code</span><span>Mobile</span><span>Full name</span><span>Email</span><span>Credits</span><span />
            </div>
            {rows.map((r) => (
              <div key={r.id} className={classNames("grid grid-cols-[96px_minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,1.6fr)_80px_40px] items-start gap-2 border-t border-gray-100 px-3 py-2", r.status === "failed" ? "bg-red-50" : r.status === "done" ? "bg-[#F3FBF6]" : "")}>
                <select aria-label="Country code" value={r.countryCode} disabled={running || r.status === "done"} onChange={(e) => update(r.id, "countryCode", e.target.value)} className={classNames(inputClass(errorsFor(r).countryCode), "h-10 rounded-xl px-2 text-sm")}>
                  {COUNTRY_CODES.concat(COUNTRY_CODES.includes(r.countryCode) ? [] : [r.countryCode]).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {cell(r, "phoneNumber", { label: "Mobile", inputMode: "tel", placeholder: "612345678" })}
                {cell(r, "fullName", { label: "Full name" })}
                {cell(r, "email", { label: "Email", placeholder: "name@email.com" })}
                {cell(r, "migrationCredit", { label: "Credits", inputMode: "numeric", placeholder: "5" })}
                <span className="flex h-10 items-center justify-center">
                  {r.status === "done" ? (
                    <Icon className="h-5 w-5 text-[#1F8F4E]" d={<path d="m5 12 5 5L20 7" />} />
                  ) : r.status === "sending" ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#E3D0F7] border-t-indigo-bz" />
                  ) : (
                    <button type="button" aria-label="Remove row" disabled={running} onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))} className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100">
                      <Icon className="h-[18px] w-[18px]" d={<><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" /></>} />
                    </button>
                  )}
                </span>
                {r.status === "failed" && r.message && <div className="col-span-6 -mt-1 text-xs font-bold text-red-700">{r.message}</div>}
              </div>
            ))}
            <div className="border-t border-gray-100 px-3 py-2">
              <button type="button" disabled={running} onClick={() => setRows((rs) => [...rs, blankRow()])} className="rounded-xl px-3 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
                + Add a row
              </button>
            </div>
          </div>
        </div>
      </Card>
    </FcDrawer>
  );
}
